/**
 * A Word document's text, read without a DOM: paragraphs on their own lines,
 * tabs and breaks kept, tracked deletions left out. Shared by the browser
 * (contract reviews read a chosen file) and the server (a workflow's Intake
 * step reads a file from SharePoint or Drive).
 *
 * @module docxText
 */
import { readZipEntries, readZipEntryText } from "./zip.ts";

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

const decodeEntities = (value: string) =>
  value.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity.startsWith("#x") || entity.startsWith("#X")) return String.fromCodePoint(Number.parseInt(entity.slice(2), 16));
    if (entity.startsWith("#")) return String.fromCodePoint(Number.parseInt(entity.slice(1), 10));
    return ENTITIES[entity.toLowerCase()] ?? match;
  });

/**
 * A Word body's text: paragraphs on their own lines, tabs and breaks kept,
 * tracked deletions (`w:del`, `w:moveFrom`) dropped. A tag scanner rather
 * than a DOM parser, so it runs (and is tested) anywhere.
 */
export function docxXmlToText(xml: string): string {
  let out = "";
  let hidden = 0;
  let inText = false;
  // A name, an optional ":local", then attributes that must start with a
  // space: every character has one place to go, so the match stays linear
  // however long a name runs (nothing for a failed match to re-split), and
  // attributes stop at the next "<" (an unclosed tag cannot swallow the rest).
  const tag = /<(\/?)([a-zA-Z0-9]+)(?::([a-zA-Z0-9]+))?(?:\s[^<>]*)?\/?>|([^<]+)/g;
  for (const match of xml.matchAll(tag)) {
    const [whole, closing, first, second, text] = match;
    const local = second ?? first;
    const selfClosing = text === undefined && whole.endsWith("/>");
    if (text !== undefined) {
      if (inText && hidden === 0) out += decodeEntities(text);
      continue;
    }
    const isHide = local === "del" || local === "moveFrom";
    if (closing) {
      if (isHide) hidden = Math.max(0, hidden - 1);
      else if (local === "t") inText = false;
      else if (local === "p" && hidden === 0) out += "\n";
      continue;
    }
    if (isHide && !selfClosing) hidden += 1;
    else if (local === "t" && !selfClosing) inText = true;
    else if (hidden === 0 && local === "tab") out += "\t";
    else if (hidden === 0 && (local === "br" || local === "cr")) out += "\n";
  }
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

/** The most of a Word body's XML read (the zip reader's own limit applies too). */
export const DOCX_XML_MAX_BYTES = 16 * 1024 * 1024;

/** A .docx file's body text; rejects when the bytes are not a Word document. */
export async function readDocxText(bytes: Uint8Array): Promise<string> {
  const entry = readZipEntries(bytes).get("word/document.xml");
  if (!entry) throw new Error("This is not a Word document.");
  return docxXmlToText(await readZipEntryText(bytes, entry, { maxBytes: DOCX_XML_MAX_BYTES }));
}
